import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Table, TableMenu, TableColumn, MenuOption } from "../../components/Table";
import { DashboardHeader } from "../../components/DashboardHeader";
import { initAllStores } from "../../utils/cache";
import { syncBillHistory } from "../../utils/sync";
import BookingDetailsModal from "../../Modal/bookings/BookingDetailModal";
import FullPageSpinner from "../../components/FullPageSpinner";

import Booking from "../../models/Booking";
import BookedRoom from "../../models/BookedRoom";
import { BookingUi } from "../../types/booking";
// import { sendRequest, } from "../../utils/api";
import Bill from "../../Modal/accounting/BillModal";
import { BillData } from "../../types/bill";
import { attachRoomDataToBookings, } from "../../utils/booking-helper";
import { PAGINATION_PAGE_NUMBER } from "../../constants";
import { formatDateTime } from "../../utils/date";
import { capitalizeFirst, capitalizeWords, padNumber } from "../../utils/string";

const columns: TableColumn[] = [
  { key: "bookingNo", label: "Book No.", },
  { key: "date", label: "Created At", responsive: "md" },
  { key: "name", label: "Guest", responsive: "md" },
  {
    key: "paymentStatus",
    label: "Payment Status",
    getClassName: (value) =>
      value === "Paid"
        ? "text-green-600 font-semibold"
        : "text-red-500 font-semibold",
    responsive: "md"
  },
  { key: "discount", label: "Discount", },
  { key: "amount", label: "Amount" }
];

const title = "Bookings";
const categories = ["Pending", "Paid"];

const BookingPage = () => {
  const navigate = useNavigate();
  // const { openModal, closeModal } = useModal();
  // const { showToast } = useToast();

  // const { handleApiConflict } = useHandleApiConflict({
  //   onReload: () => syncBooking(),
  // });

  const [search, setSearch] = useState("");
  const [bookings, setBookings] = useState<BookingUi[]>([] as BookingUi[]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showBill, setShowBill] = useState(false);
  const [bookingBill, setBookingBill] = useState<BillData>({
    issuedBy: "",
    bookings: [],
    discount: 0,
    hotel: {
      id: "",
      name: "",
      email: "",
      phone: "",
      address: "",
      preference: { vatRate: 0, serviceCharge: 0 } as any
    },
    guestName: "",
    billNumber: ""
  });

  // const [selectedGuest, setSelectedGuest] = useState<Customer>({} as Customer);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const [lastSyncedAt, setLastSyncedAt] = useState<Date | null>(null);

  const [bookingData, setBookingData] = (
    useState<Booking & { totalBeforeDiscount: number }>({} as Booking & { totalBeforeDiscount: number })
  );
  const [bookedRoomsData, setBookedRoomsData] = useState<BookedRoom[]>({} as BookedRoom[]);

  // const [showGuestModal, setShowGuestModal] = useState(false);
  // const [access, setAccess] = useState({ forceDeleteBooking: false, updateBooking: false });

  const syncBooking = useCallback(async (date?: { start: string, end: string }, search?: string) => {
    setLoading(true);
    try {
      const cache = await initAllStores();
      let allTodayHistory = [];

      const unsyncBookingData = await cache.getAllBy("bookings", { isSynced: false });
      const unsyncBookedRoomsData = await cache.getAllBy("bookedRooms", { isSynced: false });

      const { bookings } = await syncBillHistory({
        bookings: unsyncBookingData, bookedRooms: unsyncBookedRoomsData,
      }, date, search);


      // const bookings = await cache.getAllBy("bookings");
      if (!bookings) return;
      allTodayHistory = await Promise.all(bookings.map(async (booking) => {

        const bookedRooms = await cache.getAllBy("bookedRooms", { bookingId: booking.id });

        const { totalAmount, totalDiscount } = bookedRooms?.reduce(
          (acc, room: { amount?: number; discount?: number }) => {
            acc.totalAmount += room.amount || 0;
            acc.totalDiscount += room.discount || 0;
            return acc;
          },
          { totalAmount: 0, totalDiscount: 0 }
        ) || { totalAmount: 0, totalDiscount: 0 };

        const isPaid = bookedRooms.every((bookedRoom: BookedRoom) => bookedRoom.isPaid);
        return {
          createdAt: booking.createdAt,
          id: booking.id,
          name: capitalizeWords(booking.customer?.name ?? ""),
          bookingNo: padNumber(booking?.bookingNo),
          date: formatDateTime(booking.updatedAt),
          paymentStatus: isPaid ? "Paid" : "Pending",
          isPaid, duration: booking.duration,
          discount: totalDiscount.toLocaleString(),
          amount: (totalAmount).toLocaleString(),
        };
      }));

      setBookings(allTodayHistory as any);
      //      // Update last sync timestamp
      // const now = new Date().toISOString();
      // await cache.updateManyBy("data", ["lastSyncTime"], {
      //   bookings: now,
      // });

      setLastSyncedAt(new Date());
    } catch (err: any) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    syncBooking();
  }, []);


  const showBookingModal = async (bookingId: string) => {
    const cache = await initAllStores();
    try {
      setLoading(true);
      const booking: Booking | null = await cache.getBy("bookings", { id: bookingId });
      if (!booking) return;

      let bookedRooms: BookedRoom[] = await cache.getAllBy("bookedRooms", { bookingId }, "createdAt");

      bookedRooms = await attachRoomDataToBookings(bookedRooms);

      const { totalAmount, totalDiscount } = bookedRooms?.reduce(
        (acc, room: { amount?: number; discount?: number }) => {
          acc.totalAmount += room.amount || 0;
          acc.totalDiscount += room.discount || 0;
          return acc;
        },
        { totalAmount: 0, totalDiscount: 0 }
      ) || { totalAmount: 0, totalDiscount: 0 };

      if (!bookedRooms) return;

      const mapBooking = {
        ...booking,
        discount: totalDiscount,
        totalBeforeDiscount: totalAmount,
      };

      setBookingData(mapBooking);
      setBookedRoomsData(bookedRooms.reverse());
      setShowModal(true);

    } catch (err: any) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  // const cancelBooking = async (bookingId: string) => {
  //   setLoading(true);
  //   try {
  //     const cache = await initAllStores();

  //     await sendRequest("DELETE", `/bookings/${bookingId}/delete-all`);
  //     setBookings((prevBookings) =>
  //       prevBookings
  //         .filter((b: BookingUi) => b.id !== bookingId)
  //     )

  //     const bookings = await cache.getAllBy("bookedRooms", { bookingId });
  //     const allBookedRoomsId = bookings.map(b => b.roomId);
  //     Promise.all([
  //       cache.deleteOne("bookings", bookingId),
  //       cache.updateManyBy("rooms", allBookedRoomsId, { isOccupied: false }),
  //     ]);

  //     showToast(
  //       "The booking and all related room records have been successfully deleted.",
  //       { type: "success" }
  //     );

  //   } catch (error: any) {
  //     if (error.status === 404) {
  //       openModal(
  //         "Booking Not Found ❗",
  //         "We couldn’t find this booking. It may have already been deleted.",
  //         closeModal,
  //         false,
  //         "Close",
  //       );
  //     } else if (error.status === 409) {
  //       showToast("An error occurred while deleting booking. Please try again later.", { type: "error" });
  //       setTimeout(() => {
  //         syncBooking();
  //       }, DELAY_BEFORE_RELOAD);
  //     } else {
  //       openModal(
  //         "Cancellation Failed ❌",
  //         "An unexpected error occurred while cancelling the booking. Please try again later.",
  //         closeModal,
  //         false,
  //         "Close",
  //       );
  //     }

  //   } finally {
  //     setLoading(false);
  //   }
  // };


  const bookingsByDate = async (start: string, end: string) => {
    syncBooking({ start, end });
  }


  const searchBookings = () => {
    syncBooking(undefined, search)
  }


  const filteredData = bookings.filter((b: BookingUi) => {
    const matchesSearch = ( 
      b.name.toLowerCase().includes(search.toLowerCase()) ||
      b.bookingNo.includes(search)
    );
    if (selectedCategory) {
      const matchesCategory =
        (selectedCategory === "Pending" && b.paymentStatus === "Pending") ||
        (selectedCategory === "Paid" && b.paymentStatus === "Paid");
      return matchesSearch && matchesCategory;
    }
    return matchesSearch;
  });

  return (
    <div className="p-4">
      <DashboardHeader
        title={title}
        categories={categories}
        search={search}
          onClickSearch={searchBookings}
        filterByDate={true}
        onCategorySelect={setSelectedCategory}
        onDateFilterApply={(start, end) => bookingsByDate(start, end)}
        setSearch={setSearch}
        onSync={() => {
          setSearch("");
          syncBooking();
        }}
        lastSyncedAt={lastSyncedAt}
      />

      {/* Bill Modal */}
      {showBill && (
        <Bill
          billNumber={bookingBill.billNumber}
          hotel={bookingBill.hotel}
          discount={bookingBill.discount}
          guestName={bookingBill.guestName}
          issuedBy={bookingBill.issuedBy}
          bookings={bookingBill.bookings}
          orders={bookingBill.orders}
          onClose={() => setShowBill(false)}
        />
      )}

      <FullPageSpinner show={loading} />

      {showModal && (<BookingDetailsModal
        show={showModal}
        onClose={() => setShowModal(false)}
        booking={bookingData}
        bookedRooms={bookedRoomsData}
        setBookings={setBookings}
        syncBooking={syncBooking}
      // onAddRoom={() => }

      />)}

      {/* {showGuestModal && (
        <GuestInfoModal
          guest={selectedGuest}
          onClose={() => setShowGuestModal(false)}
          onSave={(data: Customer) => updateGuestData(data)}
        />
      )} */}

      {filteredData.length >= 0 && (
        <Table
          columns={columns}
          data={filteredData}
          rowsPerPage={PAGINATION_PAGE_NUMBER}
          tableMenu={(row: BookingUi, closeMenu) => (
            <TableMenu
              row={row}
              options={[
                {
                  label: "Rebook",
                  onClick: (b: BookingUi) => { navigate(`/rooms/book?bookingId=${b.id}`) },
                },
                // {
                //   label: "Guest Detail",
                //   onClick: (b: BookingUi) => guestDetail(b.id),
                // },
                // {
                //   label: "Guest Bill",
                //   onClick: (b: BookingUi) => guestBill(b.id),
                // },
                {
                  label: "Booked Rooms",
                  onClick: async (b: BookingUi) => {
                    await showBookingModal(b.id);
                    setShowModal(true);
                  },
                },
                // {
                //   label: "Delete",
                //   color: "text-red-500",
                //   onClick: (b: BookingUi) => {
                //     if (b.paymentStatus === "Paid" || b.isPaid) {
                //       if (!checkAccess("forceDeleteBooking", access, openModal, closeModal)) return;
                //     }

                // TODO: Also check if any of rooms booked isUse = false,
                //  then user need to delete individulally

                //     return openModal(
                //       "Delete Booking ⚠️",
                //       "This will cancel all rooms under this booking and cannot be undone. "
                //       + "Are you sure you want to continue?",
                //       () => cancelBooking(b.id),
                //       true, "Yes, Delete", "Cancel", "red"
                //     );
                //   },
                // },

              ].filter(Boolean) as MenuOption[]}
              closeMenu={closeMenu}
            />
          )}
        />
      )}
    </div>
  );
};
export default BookingPage;